/**
 * GWP Float Widget Component
 * Manages gift with purchase floating widget with cart monitoring
 */

class GwpFloatWidget extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.cartThreshold = parseInt(this.dataset.cartThreshold) || 9999;
    this.gwpMessage =
      this.dataset.gwpMessage || "Free gift with your purchase!";
    this.promoTitle = this.dataset.promoTitle || "Gift With Purchase";
    this.showOnlyWithDiscountCode =
      this.dataset.showOnlyWithDiscountCode === "true";
    this.requiredDiscountCode = this.dataset.requiredDiscountCode || "";

    // State
    this.isExpanded = false;
    this.isQualified = false;
    this.wasQualified = false; // Track previous qualification state
    this.gwpInCart = false;
    this.currentCartTotal = 0;
    this.hasDiscountCode = false;
    this.hasMatchingCode = false;

    // DOM References
    this.refs = {
      toggle: this.querySelector(".gwp-float__toggle"),
      panel: this.querySelector(".gwp-float__panel"),
      closeBtn: this.querySelector(".gwp-float__close"),
      progressFill: this.querySelector("[data-progress-fill]"),
      progressText: this.querySelector("[data-progress-text]"),
      addBtn: this.querySelector("[data-add-gwp]"),
      products: this.querySelectorAll(".gwp-float__product"),
    };

    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.cartPollInterval) {
      clearInterval(this.cartPollInterval);
    }
  }

  /**
   * Initialize component
   */
  init() {
    this.setupEventListeners();

    // Show widget immediately, will update based on cart
    this.dataset.hidden = "false";

    this.fetchCart();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Toggle expand/collapse
    this.refs.toggle?.addEventListener("click", this.handleToggle);
    this.refs.closeBtn?.addEventListener("click", this.handleClose);

    // Add to cart
    this.refs.addBtn?.addEventListener("click", this.handleAddToCart);

    // Cart updates - listen to multiple event types
    document.addEventListener("cart:updated", this.handleCartUpdate);
    document.addEventListener("cart:update", this.handleCartUpdate);
    document.addEventListener("cart:build", this.handleCartUpdate);
    document.addEventListener("ajaxProduct:added", () => this.fetchCart());
    document.addEventListener("cart:change", () => this.fetchCart());
    document.addEventListener("cart:refresh", () => this.fetchCart());
    document.addEventListener("product:added-to-cart", () => this.fetchCart());

    // Listen for theme-specific cart drawer events
    document.addEventListener("theme:cart:change", () => this.fetchCart());
    document.addEventListener("cart-drawer:open", () => this.fetchCart());
    document.addEventListener("cart-drawer:close", () => this.fetchCart());

    // Listen for discount-related events
    document.addEventListener("discount:applied", () => this.fetchCart());
    document.addEventListener("discount:removed", () => this.fetchCart());
    document.addEventListener("cart:discount-applied", () => this.fetchCart());
    document.addEventListener("cart:discount-removed", () => this.fetchCart());

    // Click outside to close
    document.addEventListener("click", this.handleClickOutside);

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isExpanded) {
        this.handleClose();
      }
    });

    // Poll cart periodically as fallback (every 5 seconds)
    this.cartPollInterval = setInterval(() => {
      this.fetchCart();
    }, 5000);
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    this.refs.toggle?.removeEventListener("click", this.handleToggle);
    this.refs.closeBtn?.removeEventListener("click", this.handleClose);
    this.refs.addBtn?.removeEventListener("click", this.handleAddToCart);
    document.removeEventListener("cart:updated", this.handleCartUpdate);
    document.removeEventListener("cart:update", this.handleCartUpdate);
    document.removeEventListener("cart:build", this.handleCartUpdate);
    document.removeEventListener("click", this.handleClickOutside);
  }

  /**
   * Handle toggle button click
   */
  handleToggle(e) {
    e.stopPropagation();
    this.isExpanded = !this.isExpanded;
    this.updatePanelState();
  }

  /**
   * Handle close button click
   */
  handleClose(e) {
    if (e) e.stopPropagation();
    this.isExpanded = false;
    this.updatePanelState();
  }

  /**
   * Handle click outside to close panel
   */
  handleClickOutside(e) {
    if (this.isExpanded && !this.contains(e.target)) {
      this.handleClose();
    }
  }

  /**
   * Update panel expanded/collapsed state
   */
  updatePanelState() {
    const { toggle, panel } = this.refs;

    if (this.isExpanded) {
      toggle?.setAttribute("aria-expanded", "true");
      panel?.setAttribute("aria-hidden", "false");
      this.focusTrap();
    } else {
      toggle?.setAttribute("aria-expanded", "false");
      panel?.setAttribute("aria-hidden", "true");
      // Return focus to the toggle button for accessibility
      toggle?.focus();
    }
  }

  /**
   * Simple focus trap for accessibility
   */
  focusTrap() {
    const focusableElements = this.refs.panel?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements?.length) {
      focusableElements[0]?.focus();
    }
  }

  /**
   * Fetch current cart data
   * @returns {Promise<Object|null>} Cart data or null if error
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const cart = await response.json();
        this.checkCart(cart);
        return cart;
      }
    } catch (error) {
      console.error("[GWP Float Widget] Error fetching cart:", error);
    }
    return null;
  }

  /**
   * Handle cart update event
   */
  handleCartUpdate(event) {
    if (event.detail?.cart) {
      this.checkCart(event.detail.cart);
    }
  }

  /**
   * Check cart and update widget state
   * @param {Object} cart - Shopify cart object
   */
  checkCart(cart) {
    this.currentCartTotal = cart.total_price || 0;

    // Check if discount codes are present
    this.hasDiscountCode =
      (cart.discount_codes && cart.discount_codes.length > 0) ||
      (cart.discount_applications && cart.discount_applications.length > 0);

    // Check if required discount code is applied (case-insensitive)
    this.hasMatchingCode = false;
    if (this.requiredDiscountCode) {
      const requiredCodeUpper = this.requiredDiscountCode.toUpperCase();

      // Check manual discount codes
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        this.hasMatchingCode = cart.discount_codes.some(
          (discount) => discount.code.toUpperCase() === requiredCodeUpper
        );
      }

      // If not found in manual codes, check automatic discounts
      if (!this.hasMatchingCode && cart.discount_applications && cart.discount_applications.length > 0) {
        this.hasMatchingCode = cart.discount_applications.some(
          (app) => app.title && app.title.toUpperCase() === requiredCodeUpper
        );
      }
    } else {
      // If no specific code required, any discount code qualifies
      this.hasMatchingCode = this.hasDiscountCode;
    }

    // Check if GWP products are already in cart
    this.gwpInCart = this.checkGwpInCart(cart.items || []);

    // Check if qualified
    const previouslyQualified = this.isQualified;
    this.isQualified = this.currentCartTotal >= this.cartThreshold;

    // Update UI
    this.updateProgress();
    this.updateVisibility();
    this.updateAddButton();

    // Trigger animations when qualification changes
    this.updateQualificationAnimations(previouslyQualified);

    // Auto-open panel when first qualifying
    if (this.isQualified && !previouslyQualified && !this.gwpInCart) {
      this.autoOpenPanel();
    }
  }

  /**
   * Check if GWP products are in cart
   * @param {Array} cartItems - Cart items array
   * @returns {boolean}
   */
  checkGwpInCart(cartItems) {
    const gwpVariantIds = this.getGwpVariantIds();
    const cartVariantIds = cartItems.map(item => item.variant_id);
    const hasGwp = cartItems.some((item) => gwpVariantIds.includes(item.variant_id));
    return hasGwp;
  }

  /**
   * Get GWP variant IDs from product elements
   * @returns {Array<number>}
   */
  getGwpVariantIds() {
    const products = Array.from(this.refs.products);
    const variantIds = products
      .map((product) => {
        return parseInt(product.dataset.variantId);
      })
      .filter(Boolean);

    return variantIds;
  }

  /**
   * Update progress bar and text
   */
  updateProgress() {
    const { progressFill, progressText } = this.refs;

    if (!progressFill || !progressText) return;

    // Calculate progress percentage
    const percentage = Math.min(
      (this.currentCartTotal / this.cartThreshold) * 100,
      100
    );
    progressFill.style.width = `${percentage}%`;

    // Update progress text
    if (this.isQualified) {
      if (this.gwpInCart) {
        progressText.textContent = "Free gift added to your cart!";
      } else {
        progressText.textContent = "Click below to add your free gift!";
      }
      progressText.classList.add("gwp-float__progress-text--qualified");
    } else {
      const remaining = this.cartThreshold - this.currentCartTotal;
      const formattedAmount = this.formatMoney(remaining);
      progressText.textContent = `Add ${formattedAmount} more to qualify`;
      progressText.classList.remove("gwp-float__progress-text--qualified");
    }
  }

  /**
   * Update widget visibility based on qualification and discount codes
   */
  updateVisibility() {
    // Check discount code requirement first
    if (this.showOnlyWithDiscountCode && !this.hasMatchingCode) {
      this.dataset.hidden = "true";
      this.handleClose(); // Close panel if discount code is required but not present
      return;
    }

    // Show widget if GWP not in cart (regardless of qualification status)
    // Hide only if GWP is already added
    if (!this.gwpInCart) {
      this.dataset.hidden = "false";
    } else {
      this.dataset.hidden = "true";
      this.handleClose(); // Close panel if GWP already added
    }
  }

  /**
   * Update add button state
   */
  updateAddButton() {
    const { addBtn } = this.refs;
    if (!addBtn) return;

    if (this.isQualified && !this.gwpInCart) {
      addBtn.disabled = false;
    } else {
      addBtn.disabled = true;
    }
  }

  /**
   * Update qualification animations
   * @param {boolean} previouslyQualified - Previous qualification state
   */
  updateQualificationAnimations(previouslyQualified) {
    const { toggle } = this.refs;
    const icon = toggle?.querySelector('.gwp-float__toggle-icon');

    if (!toggle || !icon) return;

    // Add/remove pulse animation based on qualification
    if (this.isQualified && !this.gwpInCart) {
      toggle.classList.add('gwp-float__toggle--qualified');

      // Trigger shake animation when first becoming qualified
      if (!previouslyQualified) {
        icon.classList.add('gwp-float__toggle-icon--shake');

        // Remove shake class after animation completes
        setTimeout(() => {
          icon.classList.remove('gwp-float__toggle-icon--shake');
        }, 600);
      }
    } else {
      toggle.classList.remove('gwp-float__toggle--qualified');
    }
  }

  /**
   * Auto-open panel when cart qualifies for free gift
   */
  autoOpenPanel() {
    // Wait a moment for animations to settle, then auto-expand
    setTimeout(() => {
      // Only open if not already expanded
      if (!this.isExpanded) {
        this.isExpanded = true;
        this.updatePanelState();
      }
    }, 800); // Delay to let shake animation complete first
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart() {
    if (!this.isQualified || this.gwpInCart) return;

    const variantIds = this.getGwpVariantIds();
    if (variantIds.length === 0) return;

    try {
      // Disable button during request
      this.refs.addBtn.disabled = true;

      // Prepare items to add (all GWP products)
      const items = variantIds.map((id) => ({
        id: id,
        quantity: 1,
        properties: {
          _gwp: "true",
          _promo: this.promoTitle,
        },
      }));

      // Add to cart
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        const data = await response.json();

        // Dispatch custom event
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: { product: data },
          })
        );

        // Dispatch additional cart events that themes commonly listen to
        document.dispatchEvent(new CustomEvent("cart:updated"));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.dispatchEvent(new CustomEvent("theme:cart:change"));

        // Fetch updated cart (this will hide the widget when GWP is detected)
        const updatedCart = await this.fetchCart();

        // Refresh the cart UI if ajaxCart is available
        if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
          window.ajaxCart.load();
        }

        // Update cart count badge
        if (updatedCart) {
          this.updateCartCount(updatedCart.item_count);
        }

        // Trigger cart drawer to open automatically
        this.openCartDrawer();
      } else {
        const error = await response.json();
        console.error("[GWP Float Widget] Error adding to cart:", error);
        this.refs.addBtn.disabled = false;
      }
    } catch (error) {
      console.error("[GWP Float Widget] Error:", error);
      this.refs.addBtn.disabled = false;
    }
  }

  /**
   * Format money with currency
   * @param {number} cents - Amount in cents
   * @returns {string}
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    const formatted = dollars.toFixed(2);

    // Use Shopify money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || "${{amount}}"
      );
    }

    // Use theme currency format if available
    if (window.theme && window.theme.moneyFormat) {
      return window.theme.moneyFormat.replace("{{amount}}", formatted);
    }

    // Fallback to basic format
    return `$${formatted}`;
  }

  /**
   * Update cart count badge
   * @param {number} itemCount - Number of items in cart
   */
  updateCartCount(itemCount) {
    // Look for common cart count selectors
    const countSelectors = [
      '#CartCount',
      '.cart-count',
      '.cart__count',
      '[data-cart-count]',
      '.header__cart-count'
    ];

    countSelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        element.textContent = itemCount;
      }
    });
  }

  /**
   * Open the cart drawer automatically
   */
  openCartDrawer() {
    // Close the GWP widget panel first
    this.handleClose();

    // Method 1: Click the cart trigger button (most reliable for this theme)
    const cartTrigger = document.querySelector('.js-cart-trigger, .js-cart-icon');
    if (cartTrigger) {
      cartTrigger.click();
      return;
    }

    // Method 2: Trigger jQuery magnificPopup if available (Venue theme uses this)
    if (window.jQuery && window.jQuery.magnificPopup) {
      const cartDrawer = document.querySelector('.js-cart-draw');
      if (cartDrawer) {
        window.jQuery.magnificPopup.open({
          items: {
            src: cartDrawer,
            type: 'inline'
          }
        });
        return;
      }
    }

    // Method 3: Dispatch event that theme might listen to
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
  }
}

// Register custom element
if (!customElements.get("gwp-float-widget")) {
  customElements.define("gwp-float-widget", GwpFloatWidget);
}
